import React, { useState } from "react";
import { api } from "./api";
import { AuditResult, FullReport, Finding, ScoreMap } from "./types";

const CATEGORY_META: { key: keyof ScoreMap; label: string; weight: string }[] = [
  { key: "schema",        label: "Schema Markup",          weight: "25%" },
  { key: "meta",          label: "Meta & On-Page",         weight: "20%" },
  { key: "conversational",label: "Conversational Content", weight: "20%" },
  { key: "blog",          label: "Blog & Content",         weight: "15%" },
  { key: "technical",     label: "Mobile & Technical",     weight: "10%" },
  { key: "images",        label: "Image Accessibility",    weight: "5%"  },
  { key: "directory",     label: "AI Directory Presence",  weight: "5%"  },
];

function bandClass(band: string) {
  if (band === "AI Ready")     return "bg-ready";
  if (band === "Mostly Ready") return "bg-mostly";
  if (band === "Needs Work")   return "bg-needs";
  return "bg-not";
}

function scoreColor(score: number) {
  if (score >= 70) return "#1B6B3A";
  if (score >= 40) return "#C07820";
  return "#B31C1C";
}

function barColor(score: number) {
  if (score >= 70) return "#1B6B3A";
  if (score >= 40) return "#C07820";
  return "#dc2626";
}

function bandDescription(band: string, domain: string): string {
  switch (band) {
    case "AI Ready":
      return `${domain} is well-optimized for AI search engines. A few tweaks could make it even stronger.`;
    case "Mostly Ready":
      return `${domain} has a solid foundation. Fixing the gaps below will put you ahead of most local competitors.`;
    case "Needs Work":
      return `${domain} is missing several key signals that AI search engines look for. The recommendations below are high-priority.`;
    default:
      return `${domain} is not yet visible to AI search engines. The good news — most of these fixes are straightforward.`;
  }
}

type Phase = "idle" | "loading" | "results" | "email-sent";

export default function AuditPage() {
  const [domain, setDomain] = useState("");
  const [phase, setPhase] = useState<Phase>("idle");
  const [error, setError] = useState("");
  const [result, setResult] = useState<AuditResult | null>(null);
  const [fullReport, setFullReport] = useState<FullReport | null>(null);
  const [email, setEmail] = useState("");
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailError, setEmailError] = useState("");

  async function handleAudit(e: React.FormEvent) {
    e.preventDefault();
    if (!domain.trim()) return;
    setError("");
    setPhase("loading");

    try {
      const data = await api.runAudit(domain.trim());
      setResult(data);
      setPhase("results");
    } catch (err: any) {
      setError(err.message || "Something went wrong. Please try again.");
      setPhase("idle");
    }
  }

  async function handleEmail(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim() || !result) return;
    setEmailError("");
    setEmailLoading(true);

    try {
      const data = await api.sendReport(result.auditId, email.trim());
      setFullReport(data);
      setPhase("email-sent");
    } catch (err: any) {
      setEmailError(err.message || "Could not send report. Please try again.");
    } finally {
      setEmailLoading(false);
    }
  }

  const findings: Finding[] = fullReport?.findings ?? result?.topFindings ?? [];
  const scores: ScoreMap | undefined = fullReport?.scores ?? result?.scores;

  return (
    <div className="page-wrap">
      <div className="trust-bar">
        Veteran-Owned&nbsp;&nbsp;|&nbsp;&nbsp;BBB Accredited&nbsp;&nbsp;|&nbsp;&nbsp;20+ Years on the Central Coast
      </div>
      <div className={`hero${phase === "idle" ? " hero-idle" : ""}`}>
        <div className="hero-logo">
          <a href="https://core6.marketing" target="_blank" rel="noopener noreferrer">
            <img src="/core6-logo.png" alt="Core6 Marketing" />
          </a>
        </div>
        <div className="hero-eyebrow">Free AI Search Audit</div>
        <h1>Is Your Website Showing Up<br />Where <span>AI Search</span> Is Looking?</h1>
        <p>
          Enter your domain. We check 7 signals AI search engines use to find and recommend local contractors — and show you exactly where you stand.
        </p>

        {phase === "idle" || phase === "results" || phase === "email-sent" ? (
          <form className="audit-form" onSubmit={handleAudit}>
            <input
              type="text"
              placeholder="yourwebsite.com"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              disabled={phase !== "idle"}
            />
            <button
              className="btn-primary"
              type="submit"
              disabled={phase !== "idle" || !domain.trim()}
            >
              {phase === "idle" ? "Run Free Audit" : "Re-run Audit"}
            </button>
          </form>
        ) : null}

        {error && <div className="error-box" style={{ marginTop: 16, maxWidth: 560, marginLeft: "auto", marginRight: "auto" }}>{error}</div>}
      </div>

      <div className="content">
        {phase === "loading" && (
          <div className="card loading-state">
            <div className="spinner" />
            <h3>Analyzing {domain}…</h3>
            <p>Checking schema, content, technical signals, and more.</p>
          </div>
        )}

        {(phase === "results" || phase === "email-sent") && result && scores && (
          <>
            {/* Score card */}
            <div className="card">
              <div className="score-section">
                <div
                  className="score-circle"
                  style={{ color: scoreColor(result.overallScore) }}
                >
                  <span className="score-number">{result.overallScore}</span>
                  <span className="score-label">out of 100</span>
                </div>
                <div className="score-info">
                  <span className={`score-band ${bandClass(result.band)}`}>
                    {result.band}
                  </span>
                  <h2>{result.domain}</h2>
                  <p>{bandDescription(result.band, result.domain)}</p>
                </div>
              </div>
            </div>

            {/* Category breakdown */}
            <div className="card categories">
              <h3>Score Breakdown</h3>
              {CATEGORY_META.map(({ key, label, weight }) => (
                <div className="category-row" key={key}>
                  <div className="category-header">
                    <span className="category-name">{label} <span style={{ color: "#9ca3af", fontWeight: 400 }}>({weight})</span></span>
                    <span className="category-score" style={{ color: barColor(scores[key]) }}>{scores[key]}/100</span>
                  </div>
                  <div className="bar-track">
                    <div
                      className="bar-fill"
                      style={{ width: `${scores[key]}%`, background: barColor(scores[key]) }}
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Findings */}
            <div className="card findings">
              {phase === "email-sent" ? (
                <div className="full-report-intro">
                  <h3>Full Recommendations ({findings.length} found)</h3>
                  <p>Your full report has been sent to <strong>{email}</strong></p>
                </div>
              ) : (
                <h3>Top {findings.length} Findings</h3>
              )}

              {findings.map((f, i) => (
                <div key={i} className={`finding-card ${f.impact}`}>
                  <div className="finding-body">
                    <div className="finding-tag">{f.category}</div>
                    <div className="finding-issue">{f.issue}</div>
                    <div className="finding-rec">{f.recommendation}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Email gate */}
            {phase === "results" && (
              <div className="card email-gate">
                <h3>Get Your Full Report</h3>
                <p>
                  We found <strong>{result.topFindings.length > 3 ? "multiple" : result.topFindings.length}</strong> issues above.
                  Enter your email to get the complete list with step-by-step fix instructions.
                </p>
                <form className="email-form" onSubmit={handleEmail}>
                  <input
                    type="email"
                    placeholder="you@yourcompany.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                  <button className="btn-primary" type="submit" disabled={emailLoading}>
                    {emailLoading ? "Sending…" : "Send Full Report"}
                  </button>
                </form>
                {emailError && <div className="error-box" style={{ marginTop: 12 }}>{emailError}</div>}
              </div>
            )}

            {phase === "email-sent" && (
              <div className="card success-box">
                ✓ Full report sent to <strong>{email}</strong> — check your inbox in the next minute.
              </div>
            )}
          </>
        )}
      </div>

      <div className="footer">
        <a href="https://core6.marketing" target="_blank" rel="noopener noreferrer">core6.marketing</a>
        {" · "}
        <a href="tel:8317899320">(831) 789-9320</a>
        {" · "}
        <a href="/admin">Admin</a>
      </div>
    </div>
  );
}
